import React, { createContext, useContext, useState, useCallback } from 'react';

// Create Loading Context
const LoadingContext = createContext();

// Loading Provider Component
export const LoadingProvider = ({ children }) => {
  const [loadingStates, setLoadingStates] = useState({});
  const [globalLoading, setGlobalLoading] = useState(false);

  // Add loading state for a specific key
  const setLoading = useCallback((key, isLoading, message = 'Loading...') => {
    setLoadingStates(prev => ({
      ...prev,
      [key]: { isLoading, message }
    }));
  }, []);

  // Remove loading state for a specific key
  const clearLoading = useCallback((key) => {
    setLoadingStates(prev => {
      const newStates = { ...prev };
      delete newStates[key];
      return newStates;
    });
  }, []);

  // Set global loading state
  const setGlobalLoadingState = useCallback((isLoading, message = 'Loading...') => {
    setGlobalLoading({ isLoading, message });
  }, []);

  // Clear global loading state
  const clearGlobalLoading = useCallback(() => {
    setGlobalLoading(false);
  }, []);

  // Check if any loading is active
  const isAnyLoading = globalLoading.isLoading || Object.values(loadingStates).some(state => state.isLoading);

  // Get current loading message
  const getCurrentLoadingMessage = () => {
    if (globalLoading.isLoading) {
      return globalLoading.message;
    }
    
    const activeLoading = Object.values(loadingStates).find(state => state.isLoading);
    return activeLoading ? activeLoading.message : 'Loading...';
  };

  // Clear all loading states
  const clearAllLoading = useCallback(() => {
    setLoadingStates({});
    setGlobalLoading(false);
  }, []);

  const value = {
    loadingStates,
    globalLoading,
    isAnyLoading,
    setLoading,
    clearLoading,
    setGlobalLoadingState,
    clearGlobalLoading,
    getCurrentLoadingMessage,
    clearAllLoading
  };

  return (
    <LoadingContext.Provider value={value}>
      {children}
    </LoadingContext.Provider>
  );
};

// Custom hook to use loading context
export const useLoading = () => {
  const context = useContext(LoadingContext);
  
  if (!context) {
    throw new Error('useLoading must be used within a LoadingProvider');
  }
  
  return context;
};

// Higher-order component for loading management
export const withLoading = (WrappedComponent, loadingKey) => {
  return function LoadingComponent(props) {
    const { setLoading, clearLoading } = useLoading();
    
    const handleSetLoading = (isLoading, message) => {
      setLoading(loadingKey, isLoading, message);
    };
    
    const handleClearLoading = () => {
      clearLoading(loadingKey);
    };
    
    return (
      <WrappedComponent 
        {...props} 
        setLoading={handleSetLoading}
        clearLoading={handleClearLoading}
      />
    );
  };
};

export default LoadingContext;
